import PageLoader from "@/components/loader/PageLoader";
import { User, useUser } from "@/contexts/UserContext";
import {
  Address,
  useAddressAutocomplete,
} from "@/utils/useAddressAutoComplete";
import { useAxiosInstance } from "@/utils/useAxiosInstance";
import { faImage, faTrashAlt } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Autocomplete } from "@react-google-maps/api";
import React, { FormEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import PhoneInput from "../common/phone-input";

const AddBranch = () => {
  const { t } = useTranslation();
  const { user, login } = useUser();
  const navigate = useNavigate();
  const [axiosInstance, loading, isFetching] = useAxiosInstance();

  const initialAddress: Address = {
    street: "",
    city: "",
    state: "",
    country: "",
    pincode: "",
  };

  const {
    address,
    setAddress,
    handlePlaceSelected,
    handleInputChange,
    isLoaded,
    loadError,
  } = useAddressAutocomplete(initialAddress);

  const [name, setName] = useState("");
  const [phoneDetails, setPhoneDetails] = useState({
    cleanedValue: "",
    error: "",
  });
  const [latitude, setLatitude] = useState<string | null>(null);
  const [longitude, setLongitude] = useState<string | null>(null);
  const [provinceCode, setProvinceCode] = useState<string | null>(null);
  const [isImageUploaded, setIsImageUploaded] = useState(false);
  const [imagePreviewUrl, setImagePreviewUrl] = useState("");
  const [imageBase64, setImageBase64] = useState<string>("");
  const [imgExtension, setImgExtension] = useState("");

  const fileInputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);

  useEffect(() => {
    console.log("Address Autocomplete Loaded:", isLoaded);
    if (loadError) {
      console.log("Error loading address autocomplete:", loadError);
    }
  }, [isLoaded, loadError]);

  const onLoadAutocomplete = (
    autocomplete: google.maps.places.Autocomplete,
  ) => {
    autocompleteRef.current = autocomplete;
    console.log("Google Autocomplete loaded", autocomplete);
  };

  const onPlaceChanged = () => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();

      const components = place.address_components || [];
      const location = place.geometry?.location;

      const updatedAddress: Address = { ...address };
      let foundProvinceCode = "";

      // Extract address components
      components.forEach((component) => {
        const types = component.types;
        if (types.includes("street_number")) {
          updatedAddress.street =
            component.long_name + " " + updatedAddress.street;
        }
        if (types.includes("route")) {
          updatedAddress.street += component.long_name;
        }
        if (types.includes("locality")) {
          updatedAddress.city = component.long_name;
        }
        if (types.includes("administrative_area_level_1")) {
          updatedAddress.state = component.long_name;
          foundProvinceCode = component.short_name; // State code (province_code)
        }
        if (types.includes("country")) {
          updatedAddress.country = component.long_name;
        }
        if (types.includes("postal_code")) {
          updatedAddress.pincode = component.long_name;
        }
      });

      // Set the latitude, longitude, and province_code
      setLatitude(location?.lat().toString() || "0.0");
      setLongitude(location?.lng().toString() || "0.0");
      setProvinceCode(foundProvinceCode || "");

      // Update the address in the state
      setAddress(updatedAddress);
      handlePlaceSelected(place);
    } else {
      console.log("Autocomplete is not loaded properly.");
    }
  };

  const fileToBase64 = (
    file: File,
    callback: (result: string | ArrayBuffer | null) => void,
  ): void => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => callback(reader.result);
    reader.onerror = (error) =>
      console.log("Error converting file to Base64:", error);
  };

  const handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      if (file.size > 20000000) {
        toast.error(
          "File size is too large. Please upload an image less than 20MB.",
        );
        return;
      }
      setImgExtension(file.name.split(".")[1]);
      setImagePreviewUrl(URL.createObjectURL(file));
      setIsImageUploaded(true);
      fileToBase64(file, (base64Result: string | ArrayBuffer | null) => {
        if (typeof base64Result === "string") {
          const base64Data = base64Result.split(",")[1];
          setImageBase64(base64Data);
        }
      });
    }
  };

  const handleRemoveImage = () => {
    setImagePreviewUrl("");
    setIsImageUploaded(false);
    setImageBase64("");

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleAddBranch = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (loading || !axiosInstance) return;
    if (!isImageUploaded || !imageBase64) {
      toast.error("Please upload a logo for the branch.");
      return;
    }
    const branchData = {
      name,
      user: user?.id,
      email: user?.email,
      phone: `+1${phoneDetails.cleanedValue}`,
      address: {
        street: address.street,
        city: address.city,
        latitude: latitude ? parseFloat(latitude) : "0.0",
        longitude: longitude ? parseFloat(longitude) : "0.0",
        province: address.state,
        province_code: provinceCode || "TX",
        country: address.country,
        country_code: "US",
        postal_code: address.pincode,
      },
      logo: imageBase64,
      logo_extension: imgExtension,
    };

    try {
      const apiUrl = `${import.meta.env.VITE_APP_API_BASE_URL}${
        import.meta.env.VITE_APP_REDBIRDPOSBE_BRANCHES
      }`;

      await axiosInstance.post(apiUrl, JSON.stringify(branchData), {
        headers: {
          "Content-Type": "application/json",
        },
      });

      const userBranchesResponse = await axiosInstance.get(
        `${apiUrl}?user=${user?.id}`,
      );
      const userBranches = userBranchesResponse.data.results;

      const storedUserData = localStorage.getItem("user_data");
      if (storedUserData) {
        const userData: User = JSON.parse(storedUserData);
        userData.branches = userBranches;
        localStorage.setItem("user_data", JSON.stringify(userData));
        login(userData);
      }

      setName("");
      setLatitude("");
      setLongitude("");
      setPhoneDetails({ cleanedValue: "", error: "" });
      setImagePreviewUrl("");
      setIsImageUploaded(false);
      setImageBase64("");
      setAddress(initialAddress); // Reset address
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      toast.success("Branch added successfully.");
      navigate("/dashboard");
    } catch (error) {
      console.error("Error adding branch:", error);
      toast.error("Error adding branch. Please try again.");
    }
  };

  const handleAddressChange = (field: keyof Address, value: string) =>
    handleInputChange(field, value);

  return (
    <PageLoader isFetching={isFetching}>
      <div className="container mx-auto px-4 py-2">
        <h1 className="text-xl font-bold">
          {t("back.management.dashboard.branch.addnewbranch")}
        </h1>

        <form onSubmit={handleAddBranch} className="mt-4">
          <div className="my-4 rounded-lg bg-white p-4 shadow">
            <h2 className="mb-4 text-lg font-semibold">
              {t("back.management.dashboard.branch.generalinfo")}
            </h2>
            <div className="-mx-2 flex flex-wrap">
              <div className="mb-4 w-full px-2 md:w-1/2">
                <label
                  className="mb-2 block text-sm font-bold text-gray-700"
                  htmlFor="name"
                >
                  {t("back.management.dashboard.branch.branchname")}
                  <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  required
                  placeholder={t(
                    "back.management.dashboard.branch.branchnameplaceholder",
                  )}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none focus:ring"
                />
              </div>

              <div className="mb-4 w-full px-2 md:w-1/2">
                <PhoneInput
                  label={t("back.management.dashboard.branch.phone")}
                  defaultValue={phoneDetails.cleanedValue}
                  onChange={(phone) => setPhoneDetails(phone)}
                />
              </div>

              <div className="mb-4 w-full px-2 md:w-1/2">
                <label
                  className="mb-2 block text-sm font-bold text-gray-700"
                  htmlFor="street"
                >
                  {t("back.management.dashboard.branch.street")}
                  <span className="text-red-500">*</span>
                </label>
                {isLoaded ? (
                  <Autocomplete
                    onLoad={onLoadAutocomplete}
                    onPlaceChanged={onPlaceChanged}
                  >
                    <input
                      type="text"
                      id="street"
                      required
                      placeholder={t(
                        "back.management.dashboard.branch.streetplaceholder",
                      )}
                      value={address.street}
                      onChange={(e) =>
                        handleAddressChange("street", e.target.value)
                      }
                      className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none focus:ring"
                    />
                  </Autocomplete>
                ) : (
                  <input
                    type="text"
                    id="street"
                    placeholder={t(
                      "back.management.dashboard.branch.streetplaceholder",
                    )}
                    value={address.street}
                    onChange={(e) =>
                      handleAddressChange("street", e.target.value)
                    }
                    className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none focus:ring"
                  />
                )}
              </div>

              <div className="mb-4 w-full px-2 md:w-1/2">
                <label
                  className="mb-2 block text-sm font-bold text-gray-700"
                  htmlFor="city"
                >
                  {t("back.management.dashboard.branch.city")}
                  <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="city"
                  required
                  placeholder={t("Enter City")}
                  value={address.city}
                  onChange={(e) => handleAddressChange("city", e.target.value)}
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none focus:ring"
                />
              </div>

              <div className="mb-4 w-full px-2 md:w-1/2">
                <label
                  className="mb-2 block text-sm font-bold text-gray-700"
                  htmlFor="state"
                >
                  {t("back.management.dashboard.branch.state")}
                  <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="state"
                  placeholder={t("Enter State")}
                  required
                  value={address.state}
                  onChange={(e) => handleAddressChange("state", e.target.value)}
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none focus:ring"
                />
              </div>

              <div className="mb-4 w-full px-2 md:w-1/2">
                <label
                  className="mb-2 block text-sm font-bold text-gray-700"
                  htmlFor="country"
                >
                  {t("back.management.dashboard.branch.country")}
                  <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="country"
                  placeholder={t("Enter Country")}
                  required
                  value={address.country}
                  onChange={(e) =>
                    handleAddressChange("country", e.target.value)
                  }
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none focus:ring"
                />
              </div>

              <div className="mb-4 w-full px-2 md:w-1/2">
                <label
                  className="mb-2 block text-sm font-bold text-gray-700"
                  htmlFor="pincode"
                >
                  {t("back.management.dashboard.branch.zipCode")}
                  <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="pincode"
                  required
                  placeholder={t("Enter Postal Code")}
                  value={address.pincode}
                  onChange={(e) =>
                    handleAddressChange("pincode", e.target.value)
                  }
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none focus:ring"
                />
              </div>
            </div>
          </div>

          <div className="my-4 rounded-lg bg-white p-4 shadow">
            <h1 className="mb-4 text-lg font-semibold">
              {t("back.management.dashboard.branch.imageupload")}
            </h1>
            <div className="w-full px-2">
              <p className="mb-2 mt-4 block text-sm font-bold text-gray-700">
                {t("back.management.dashboard.branch.uploadimage")}
                <span className="text-red-500">*</span>
              </p>
              <div
                className={`block w-full border-2 ${
                  isImageUploaded ? "border-solid" : "border-dotted"
                } relative flex h-48 cursor-pointer items-center justify-center rounded-md border-gray-300 shadow-sm hover:border-gray-500`}
              >
                <input
                  ref={fileInputRef}
                  id="fileUpload"
                  type="file"
                  accept="image/*"
                  className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                  onChange={handleFileChange}
                />
                {imagePreviewUrl ? (
                  <>
                    <img
                      src={imagePreviewUrl}
                      alt="Preview"
                      className="max-h-full max-w-full p-2"
                    />
                    <button
                      onClick={handleRemoveImage}
                      className="absolute right-0 top-0 m-2 rounded-full bg-red-500 p-1 text-white"
                      style={{ width: "30px", height: "30px" }}
                    >
                      <FontAwesomeIcon icon={faTrashAlt} />
                    </button>
                  </>
                ) : (
                  <div className="text-center">
                    <FontAwesomeIcon
                      icon={faImage}
                      size="2x"
                      className="mb-2 text-gray-400"
                    />
                    <p className="mb-2 text-gray-600">
                      {t("back.management.dashboard.branch.uploadimagehint")}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="mt-4 flex justify-end">
            <Link
              to="/dashboard"
              className="mr-2 rounded-md bg-gray-500 px-4 py-2 text-white"
            >
              {t("back.management.dashboard.branch.cancelbutton")}
            </Link>
            <button
              type="submit"
              className="rounded-md bg-red-500 px-4 py-2 text-white"
            >
              {t("back.management.dashboard.branch.confirmbutton")}
            </button>
          </div>
        </form>
      </div>
    </PageLoader>
  );
};

export default AddBranch;
